import { tv } from 'tailwind-variants';

import { createInlineTextComponent, type InlineTextProps } from './utils';

const bodyVariants = tv({
    base: ['font-kalshi-sans', 'font-normal'],
    variants: {
        size: {
            x10: 'typ-body-x10',
            x20: 'typ-body-x20',
            x30: 'typ-body-x30',
            x40: 'typ-body-x40',
        },
        medium: {
            true: 'font-medium',
        },
        semibold: {
            true: 'font-semibold',
        },
        tabularNums: {
            true: 'typ-tabular',
        },
        underline: {
            true: 'underline-link',
        },
    },
    defaultVariants: {
        tabularNums: false,
        underline: false,
    },
});

function createBodySize(size: string) {
    return Object.assign(createInlineTextComponent(bodyVariants, `Body.${size}`, size), {
        medium: createInlineTextComponent(bodyVariants, `Body.${size}.medium`, size, { medium: true }),
        semibold: createInlineTextComponent(bodyVariants, `Body.${size}.semibold`, size, { semibold: true }),
    });
}

/**
 * Body Text Components
 *
 * Regular weight body text with optional medium/semibold variants.
 * All variants use KalshiSans font with consistent font feature settings.
 *
 * @example
 * <Body.x20>Regular body text</Body.x20>
 * <Body.x20 tabularNums>$1,234.56</Body.x20>
 * <Body.x20.medium>Medium weight text</Body.x20.medium>
 */
export const Body = {
    /** Body x10 - 12px/16px */
    x10: createBodySize('x10'),
    /** Body x20 - 13px/18px */
    x20: createBodySize('x20'),
    /** Body x30 - 15px/22px */
    x30: createBodySize('x30'),
    /** Body x40 - 18px/26px */
    x40: createBodySize('x40'),
} as const;

export default Body;
export { bodyVariants };
export type BodyProps = InlineTextProps;
